/**
 * AI聊天Vuex模块
 * 管理AI对话状态和数据
 */

const state = {
  // 聊天窗口是否显示
  isVisible: false,
  // 聊天消息列表
  messages: [],
  // 是否正在输入
  isTyping: false,
  // 聊天按钮位置
  buttonPosition: {
    x: window.innerWidth - 90,
    y: window.innerHeight - 150
  },
  // 是否正在拖拽
  isDragging: false,
  // 欢迎消息
  welcomeMessage: {
    sender: 'ai',
    content: '您好！我是非遗索思AI助手。我可以帮您了解中华非物质文化遗产的相关知识，包括各种传统工艺、历史文化背景等。请问有什么想了解的吗？',
    timestamp: Date.now()
  }
}

const mutations = {
  /**
   * 切换聊天窗口显示状态
   * @param {Object} state - 状态对象
   * @param {boolean} visible - 是否显示
   */
  SET_CHAT_VISIBILITY(state, visible) {
    state.isVisible = visible
  },

  /**
   * 添加消息
   * @param {Object} state - 状态对象
   * @param {Object} message - 消息对象
   */
  ADD_MESSAGE(state, message) {
    state.messages.push({
      ...message,
      id: Date.now() + Math.random(),
      timestamp: Date.now()
    })
  },

  /**
   * 设置打字状态
   * @param {Object} state - 状态对象
   * @param {boolean} typing - 是否正在打字
   */
  SET_TYPING_STATUS(state, typing) {
    state.isTyping = typing
  },

  /**
   * 设置按钮位置
   * @param {Object} state - 状态对象
   * @param {Object} position - 位置坐标
   */
  SET_BUTTON_POSITION(state, position) {
    state.buttonPosition = position
  },

  /**
   * 设置拖拽状态
   * @param {Object} state - 状态对象
   * @param {boolean} dragging - 是否正在拖拽
   */
  SET_DRAGGING_STATUS(state, dragging) {
    state.isDragging = dragging
  },

  /**
   * 清空消息
   * @param {Object} state - 状态对象
   */
  CLEAR_MESSAGES(state) {
    state.messages = []
  },

  /**
   * 初始化聊天
   * @param {Object} state - 状态对象
   */
  INIT_CHAT(state) {
    if (state.messages.length === 0) {
      state.messages.push(state.welcomeMessage)
    }
  }
}

const actions = {
  /**
   * 打开聊天窗口
   * @param {Object} context - Vuex context
   */
  openChat({ commit }) {
    commit('SET_CHAT_VISIBILITY', true)
    commit('INIT_CHAT')
  },

  /**
   * 关闭聊天窗口
   * @param {Object} context - Vuex context
   */
  closeChat({ commit }) {
    commit('SET_CHAT_VISIBILITY', false)
  },

  /**
   * 发送消息
   * @param {Object} context - Vuex context
   * @param {string} content - 消息内容
   */
  async sendMessage({ commit, dispatch }, content) {
    if (!content.trim()) return

    // 添加用户消息
    commit('ADD_MESSAGE', {
      sender: 'user',
      content: content.trim()
    })

    // 设置AI正在输入状态
    commit('SET_TYPING_STATUS', true)

    try {
      // 模拟AI回复延迟
      await new Promise(resolve => setTimeout(resolve, 1000 + Math.random() * 2000))
      
      // 生成AI回复
      const aiReply = await dispatch('generateAIReply', content)
      
      // 添加AI回复
      commit('ADD_MESSAGE', {
        sender: 'ai',
        content: aiReply
      })
    } catch (error) {
      console.error('AI回复生成失败:', error)
      commit('ADD_MESSAGE', {
        sender: 'ai',
        content: '抱歉，我现在遇到了一些技术问题，请稍后再试。'
      })
    } finally {
      commit('SET_TYPING_STATUS', false)
    }
  },

  /**
   * 生成AI回复
   * @param {Object} context - Vuex context
   * @param {string} userMessage - 用户消息
   * @returns {string} AI回复
   */
  async generateAIReply({ rootGetters }, userMessage) {
    const message = userMessage.toLowerCase()

    // 基于关键词的简单回复逻辑
    if (message.includes('龙舟')) {
      return '龙舟是中华民族的传统水上运动项目，融合了流体力学的造船技术。龙舟竞渡不仅是体育竞技，更是传承了深厚的文化内涵。您想了解龙舟的哪个方面呢？比如制作工艺、历史起源或者比赛规则？'
    }

    if (message.includes('剪纸')) {
      return '剪纸是中国传统的民间艺术形式，体现了几何学与材料学的完美结合。通过一把剪刀和一张纸，就能创造出精美的图案。剪纸技艺讲究对称美和镂空艺术，您想了解剪纸的制作技巧还是文化寓意呢？'
    }

    if (message.includes('皮影')) {
      return '皮影戏是运用光学原理的传统戏剧艺术形式，通过灯光投射产生神奇的光影效果。这门艺术巧妙地利用了光的直线传播原理，在幕布上呈现生动的故事。您对皮影的制作工艺还是表演技巧更感兴趣？'
    }

    if (message.includes('茶') || message.includes('英德')) {
      return '英德红茶制作技艺运用了生物发酵学原理，通过控制茶叶的发酵过程来形成独特的风味。这个过程涉及微生物的作用和酶的催化反应。您想了解茶叶的发酵原理还是品鉴方法呢？'
    }

    if (message.includes('泥塑') || message.includes('大吴')) {
      return '大吴泥塑运用地质学原理，利用天然陶土的可塑性创造艺术品。这门技艺需要深入了解土质特性和烧制温度控制。您想了解泥塑的制作流程还是艺术特色？'
    }

    if (message.includes('象牙雕')) {
      return '象牙雕是体现精密加工技术的传统雕刻艺术，需要高超的技艺和对材料特性的深度理解。现在我们更提倡保护动物，可以用替代材料来传承这门工艺技术。您想了解雕刻技法还是现代替代材料？'
    }

    if (message.includes('石狗') || message.includes('雷州')) {
      return '雷州石狗体现了地质学与文化学的结合，是当地独特的石雕艺术。工匠们利用当地的石材特性，雕刻出形态各异的石狗，每一个都有其特殊的文化含义。您想了解石狗的文化寓意还是雕刻技艺？'
    }

    if (message.includes('毛笔') || message.includes('白沙')) {
      return '白沙茅龙笔制作技艺运用生物材料科学，精选动物毛料制成优质毛笔。这门工艺需要了解不同动物毛的特性和处理方法。您想了解毛笔的制作工艺还是使用技巧？'
    }

    if (message.includes('学习') || message.includes('怎么')) {
      return '学习非遗文化可以从多个角度入手：\n1. 了解背景知识和文化内涵\n2. 观看制作工艺视频\n3. 参与体验活动\n4. 与传承人交流学习\n\n我们平台提供了丰富的学习资源，您可以根据兴趣选择不同的非遗项目进行深入学习。'
    }

    if (message.includes('谢谢') || message.includes('感谢')) {
      return '不客气！很高兴能帮助您了解中华非遗文化。如果还有其他问题，随时可以问我。让我们一起传承和弘扬这些珍贵的文化瑰宝！'
    }

    // 默认回复
    const defaultReplies = [
      '这是一个很有意思的问题！中华非遗文化博大精深，每一项都有其独特的科学原理和文化内涵。您可以通过我们的分类浏览来深入了解。',
      '我理解您对非遗文化的关注。建议您可以从感兴趣的类别开始，比如物质科学、生命科学等，每个类别都有详细的介绍和学习资源。',
      '非遗文化的传承需要我们每个人的参与。您可以通过学习、体验、分享等方式来参与其中。有什么具体想了解的项目吗？'
    ]

    return defaultReplies[Math.floor(Math.random() * defaultReplies.length)]
  },

  /**
   * 更新按钮位置
   * @param {Object} context - Vuex context
   * @param {Object} position - 位置坐标
   */
  updateButtonPosition({ commit }, position) {
    commit('SET_BUTTON_POSITION', position)
  },

  /**
   * 设置拖拽状态
   * @param {Object} context - Vuex context
   * @param {boolean} dragging - 拖拽状态
   */
  setDraggingStatus({ commit }, dragging) {
    commit('SET_DRAGGING_STATUS', dragging)
  },

  /**
   * 清空聊天记录
   * @param {Object} context - Vuex context
   */
  clearChat({ commit }) {
    commit('CLEAR_MESSAGES')
    commit('INIT_CHAT')
  }
}

const getters = {
  /**
   * 获取聊天可见状态
   * @param {Object} state - 状态对象
   * @returns {boolean} 是否可见
   */
  isChatVisible: state => state.isVisible,

  /**
   * 获取消息列表
   * @param {Object} state - 状态对象
   * @returns {Array} 消息列表
   */
  getMessages: state => state.messages,

  /**
   * 获取打字状态
   * @param {Object} state - 状态对象
   * @returns {boolean} 是否正在打字
   */
  isTyping: state => state.isTyping,

  /**
   * 获取按钮位置
   * @param {Object} state - 状态对象
   * @returns {Object} 按钮位置
   */
  getButtonPosition: state => state.buttonPosition,

  /**
   * 获取拖拽状态
   * @param {Object} state - 状态对象
   * @returns {boolean} 是否正在拖拽
   */
  isDragging: state => state.isDragging,

  /**
   * 获取最新消息
   * @param {Object} state - 状态对象
   * @returns {Object} 最新消息
   */
  getLatestMessage: state => {
    return state.messages.length > 0 ? state.messages[state.messages.length - 1] : null
  }
}

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters
} 